import React, { useState, useCallback } from 'react';
import {
    Container, Box, Typography, Paper, TextField, FormControl, FormLabel,
    RadioGroup, FormControlLabel, Radio, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress, Alert, Card, CardContent
} from '@mui/material';
import { format } from 'date-fns';

// --- Tipos ---
interface LinhaTabela {
    mesAno: string;
    saldoInicialMes: number;
    aporteDoMes: number;
    taxaMensal: number;
    taxaAdicionalAplicada: number;
    jurosDoMes: number;
    saldoAtual: number;
}

interface ResultadoCalculo {
    totalInvestido: number;
    totalJuros: number;
    valorFinal: number;
    linhas: LinhaTabela[];
}

// --- Funções Utilitárias ---
const parseCurrency = (value: string): number => {
    if (!value) return 0;
    // Remove pontos de milhar e substitui vírgula de decimal por ponto
    return parseFloat(value.replace(/\./g, '').replace(',', '.'));
};

const formatCurrency = (value: number): string => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatPercent = (value: number): string => {
    return (value * 100).toFixed(2).replace('.', ',') + '%';
};

const formatInputAsCurrency = (value: string): string => {
    if (!value) return '';
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly === '') return '';
    const intValue = parseInt(digitsOnly, 10);
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(intValue / 100);
};

// --- Componente Principal ---
const AtualizacaoMonetariaPage: React.FC = () => {
    // --- Estados do Formulário ---
    const [tipoInvestimento, setTipoInvestimento] = useState('unico');
    const [tipoCorrecao, setTipoCorrecao] = useState('selic');
    const [valorInput, setValorInput] = useState('');
    const [dataInicial, setDataInicial] = useState('2020-01');
    const [dataFinal, setDataFinal] = useState(format(new Date(), 'yyyy-MM'));
    const [taxaManual, setTaxaManual] = useState('');
    const [taxaAdicionalSelic, setTaxaAdicionalSelic] = useState('100');

    // --- Estados de Controle e Resultado ---
    const [resultado, setResultado] = useState<ResultadoCalculo | null>(null);
    const [calculando, setCalculando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    // --- Lógica da API SELIC ---
    const fetchSelicRates = useCallback(async (startDate: string, endDate: string) => {
        const initialDate = new Date(startDate + '-02');
        const finalDate = new Date(endDate + '-02');
        const ddi = format(initialDate, 'dd/MM/yyyy');
        const ddf = format(finalDate, 'dd/MM/yyyy');

        const url = `https://api.bcb.gov.br/dados/serie/bcdata.sgs.4390/dados?formato=json&dataInicial=${ddi}&dataFinal=${ddf}`;

        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error(`Erro na API do BCB: ${response.statusText}`);
            const data = await response.json();
            const rates = new Map<string, number>();
            data.forEach((item: { data: string; valor: string }) => {
                const [, month, year] = item.data.split('/');
                rates.set(`${year}-${month}`, parseFloat(item.valor) / 100);
            });
            return rates;
        } catch (error) {
            console.error("Falha ao buscar dados da SELIC:", error);
            setErro(error instanceof Error ? error.message : 'Falha ao buscar dados da SELIC.');
            return null;
        }
    }, []);

    // --- Lógica Principal de Cálculo ---
    const handleCalcular = useCallback(async () => {
        setCalculando(true);
        setErro(null);
        setResultado(null);

        try {
            const valor = parseCurrency(valorInput);
            const taxaAdicionalSelicValue = parseFloat(taxaAdicionalSelic.replace(',', '.')) || 100;
            const taxaAdicionalSelicMultiplier = tipoCorrecao === 'selic' ? (taxaAdicionalSelicValue / 100) : 1; // Usa 1 como multiplicador se não for SELIC

            if (valor <= 0 || !dataInicial || !dataFinal) {
                setErro("Por favor, preencha o valor, data inicial e data final.");
                return;
            }
            if (new Date(dataInicial + '-02') > new Date(dataFinal + '-02')) {
                setErro("A data inicial não pode ser posterior à data final.");
                return;
            }

            let selicRates: Map<string, number> | null = null;
            if (tipoCorrecao === 'selic') {
                selicRates = await fetchSelicRates(dataInicial, dataFinal);
                if (!selicRates) return; // Erro já foi setado no fetch
            }

            let saldoAtual = 0;
            let totalInvestido = 0;
            let totalJuros = 0;
            const linhas: LinhaTabela[] = [];
            const isMensal = tipoInvestimento === 'mensal';

            if (!isMensal) {
                saldoAtual = valor;
                totalInvestido = valor;
            }

            let dataCorrente = new Date(dataInicial + '-02');
            const dataFinalDate = new Date(dataFinal + '-02');

            while (dataCorrente <= dataFinalDate) {
                const saldoInicialMes = saldoAtual;
                const aporteDoMes = isMensal ? valor : 0;
                if (isMensal) {
                    saldoAtual += aporteDoMes;
                    totalInvestido += aporteDoMes;
                }

                let taxaMensal = 0;
                if (tipoCorrecao === 'manual') {
                    taxaMensal = parseCurrency(taxaManual) / 100;
                } else if (selicRates) {
                    const key = format(dataCorrente, 'yyyy-MM');
                    taxaMensal = selicRates.get(key) || 0;
                }

                const taxaTotalMes = taxaMensal * taxaAdicionalSelicMultiplier;
                const jurosDoMes = saldoAtual * taxaTotalMes;

                totalJuros += jurosDoMes;
                saldoAtual += jurosDoMes;

                linhas.push({
                    mesAno: format(dataCorrente, 'MM/yyyy'),
                    saldoInicialMes,
                    aporteDoMes,
                    taxaMensal,
                    taxaAdicionalAplicada: taxaAdicionalSelicMultiplier,
                    jurosDoMes,
                    saldoAtual,
                });

                dataCorrente.setMonth(dataCorrente.getMonth() + 1);
            }

            setResultado({ totalInvestido, totalJuros, valorFinal: saldoAtual, linhas });

        } finally {
            setCalculando(false);
        }
    }, [valorInput, dataInicial, dataFinal, tipoCorrecao, tipoInvestimento, taxaManual, taxaAdicionalSelic, fetchSelicRates]);

    const handleImprimir = () => {
        window.print();
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper sx={{ p: 3, '@media print': { boxShadow: 'none', m: 0, p: 0 } }}>
                <Box id="form-section-wrapper" className="form-container" sx={{ '@media print': { display: 'none' } }}>
                    <Typography variant="h4" gutterBottom>
                        Calculadora de Atualização Monetária
                    </Typography>

                    {/* Seção 1: Tipo de Cálculo */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">1. Tipo de Cálculo</FormLabel>
                            <RadioGroup row value={tipoInvestimento} onChange={(e) => setTipoInvestimento(e.target.value)}>
                                <FormControlLabel value="unico" control={<Radio />} label="Valor Único" />
                                <FormControlLabel value="mensal" control={<Radio />} label="Aportes Mensais" />
                            </RadioGroup>
                        </FormControl>
                    </Paper>

                    {/* Seção 2: Dados para Cálculo */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                        <FormLabel component="legend" sx={{ mb: 2 }}>2. Dados para Cálculo</FormLabel>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(33.33% - 16px)' } }}>
                                <TextField
                                    label={tipoInvestimento === 'mensal' ? 'Aporte Mensal (R$)' : 'Valor Inicial (R$)'}
                                    fullWidth
                                    value={valorInput}
                                    onChange={(e) => setValorInput(formatInputAsCurrency(e.target.value))}
                                    placeholder="1.000,00"
                                />
                            </Box>
                            <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(33.33% - 16px)' } }}>
                                <TextField
                                    label="Data Inicial"
                                    type="month"
                                    fullWidth
                                    value={dataInicial}
                                    onChange={(e) => setDataInicial(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>
                            <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(33.33% - 16px)' } }}>
                                <TextField
                                    label="Data Final"
                                    type="month"
                                    fullWidth
                                    value={dataFinal}
                                    onChange={(e) => setDataFinal(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>
                        </Box>
                    </Paper>

                    {/* Seção 3: Tipo de Correção */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">3. Tipo de Correção</FormLabel>
                            <RadioGroup row value={tipoCorrecao} onChange={(e) => setTipoCorrecao(e.target.value)}>
                                <FormControlLabel value="selic" control={<Radio />} label="Taxa SELIC (Automático)" />
                                <FormControlLabel value="manual" control={<Radio />} label="Taxa Manual" />
                            </RadioGroup>
                        </FormControl>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
                            {tipoCorrecao === 'manual' && (
                                <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(50% - 16px)' } }}>
                                    <TextField
                                        label="Taxa de Juros Mensal (%)"
                                        fullWidth
                                        value={taxaManual}
                                        onChange={(e) => setTaxaManual(formatInputAsCurrency(e.target.value))}
                                        placeholder="1,00"
                                    />
                                </Box>
                            )}
                            {tipoCorrecao === 'selic' && (
                                <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(50% - 16px)' } }}>
                                    <TextField
                                        label="Taxa sobre a SELIC (%)"
                                        fullWidth
                                        value={taxaAdicionalSelic}
                                        onChange={(e) => setTaxaAdicionalSelic(e.target.value)}
                                        placeholder="100,00"
                                    />
                                </Box>
                            )}
                        </Box>
                    </Paper>

                    {/* Ações */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCalcular}
                            disabled={calculando}
                            startIcon={calculando ? <CircularProgress size={20} /> : null}
                        >
                            {calculando ? 'Calculando...' : 'Calcular'}
                        </Button>
                        <Button variant="outlined" onClick={handleImprimir} disabled={!resultado}>
                            Imprimir Relatório
                        </Button>
                    </Box>
                </Box>

                {/* Seção de Erro */}
                {erro && <Alert severity="error" sx={{ mt: 3 }}>{erro}</Alert>}

                {/* Seção de Resultados */}
                {resultado && (
                    <Box id="results-section" sx={{ mt: 5, '@media print': { display: 'block !important', mt: 0 } }}>
                        <Typography variant="h5" gutterBottom align="center">
                            Relatório de Atualização Monetária
                        </Typography>

                        {/* Resumo */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, mb: 4, justifyContent: 'center' }}>
                            <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(33.33% - 10px)' } }}>
                                <Card variant="outlined">
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography variant="overline" color="text.secondary">Total Investido</Typography>
                                        <Typography variant="h6">{formatCurrency(resultado.totalInvestido)}</Typography>
                                    </CardContent>
                                </Card>
                            </Box>
                            <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(33.33% - 10px)' } }}>
                                <Card variant="outlined">
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography variant="overline" color="text.secondary">Total em Juros</Typography>
                                        <Typography variant="h6">{formatCurrency(resultado.totalJuros)}</Typography>
                                    </CardContent>
                                </Card>
                            </Box>
                            <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(33.33% - 10px)' } }}>
                                <Card variant="outlined">
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography variant="overline" color="text.secondary">Valor Final</Typography>
                                        <Typography variant="h5" component="p">{formatCurrency(resultado.valorFinal)}</Typography>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Box>

                        {/* Tabela Detalhada */}
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Mês/Ano</TableCell>
                                        <TableCell align="right">Saldo Inicial</TableCell>
                                        {tipoInvestimento === 'mensal' && <TableCell align="right">Aporte</TableCell>}
                                        <TableCell align="right">Taxa (Mês)</TableCell>
                                        {tipoCorrecao === 'selic' && <TableCell align="right">Taxa sobre SELIC</TableCell>}
                                        <TableCell align="right">Juros (R$)</TableCell>
                                        <TableCell align="right">Saldo Final</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {resultado.linhas.map((linha, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{linha.mesAno}</TableCell>
                                            <TableCell align="right">{formatCurrency(linha.saldoInicialMes)}</TableCell>
                                            {tipoInvestimento === 'mensal' && <TableCell align="right">{formatCurrency(linha.aporteDoMes)}</TableCell>}
                                            <TableCell align="right">{formatPercent(linha.taxaMensal)}</TableCell>
                                            {tipoCorrecao === 'selic' && <TableCell align="right">{formatPercent(linha.taxaAdicionalAplicada)}</TableCell>}
                                            <TableCell align="right">{formatCurrency(linha.jurosDoMes)}</TableCell>
                                            <TableCell align="right">{formatCurrency(linha.saldoAtual)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default AtualizacaoMonetariaPage;